import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { LayoutDashboard, Search, Sparkles, Wrench, FileText, BarChart3 } from "lucide-react";
import { Reveal } from "@/components/site/Motion";
import { Label, H2, Paras, CtaButton } from "@/components/site/Blocks";

const NAV = [[LayoutDashboard, "Overview"], [Search, "SEO"], [Sparkles, "AI Search"], [Wrench, "Technical"], [FileText, "Content"], [BarChart3, "Reports"]];
const TILES = ["Organic visibility", "Organic traffic", "Enquiries & conversions", "AI mentions"];
const POINTS = [
  ["Everything in one place", "All of the work we are doing for you, and the results it is producing, visible in one dedicated platform."],
  ["Full transparency", "See what has been done, what is in progress and what is coming next."],
  ["Google and AI search", "Track performance across traditional search and the AI search experiences that matter to your business."],
];

const spark = (seed) => Array.from({ length: 12 }, (_, i) => 40 - (i * 2.4 + Math.sin(i * 1.3 + seed) * 5)).map((y, i) => `${i * 10},${y.toFixed(1)}`).join(" ");

const Mock = () => (
  <div className="overflow-hidden rounded-[22px] border border-white/10 bg-[#0f0a22] shadow-[0_60px_120px_-40px_rgba(76,29,149,0.6)]">
    <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
      <span className="h-2.5 w-2.5 rounded-full bg-white/20" /><span className="h-2.5 w-2.5 rounded-full bg-white/20" /><span className="h-2.5 w-2.5 rounded-full bg-violet-400" />
      <span className="ml-3 hidden rounded-full bg-white/5 px-4 py-1 font-mono text-[11px] text-slate-400 sm:inline">Perfect Digitals · Reporting Platform</span>
    </div>
    <div className="grid grid-cols-12">
      <aside className="col-span-3 hidden border-r border-white/10 p-4 md:block">
        <img src="/brand/logo-white.png" alt="Perfect Digitals" className="mb-6 h-6 w-auto" />
        {NAV.map(([Icon, l], i) => (
          <div key={l} className={`mb-1 flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] ${i === 0 ? "bg-violet-500/20 text-white" : "text-slate-400"}`}><Icon className="h-3.5 w-3.5" />{l}</div>
        ))}
      </aside>
      <div className="col-span-12 space-y-3 p-4 sm:p-5 md:col-span-9">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {TILES.map((t, i) => (
            <div key={t} className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
              <p className="text-[12px] text-slate-400">{t}</p>
              <svg viewBox="0 0 110 44" className="mt-2 h-10 w-full" aria-hidden="true">
                <polyline points={spark(i)} fill="none" stroke={i % 2 ? "#818CF8" : "#A78BFA"} strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
          ))}
        </div>
        <div className="grid gap-3 lg:grid-cols-5">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 lg:col-span-3">
            <p className="text-[12px] text-slate-400">Search visibility · Google &amp; AI</p>
            <div className="mt-4 flex h-32 items-end gap-1.5">
              {Array.from({ length: 16 }).map((_, i) => (
                <div key={i} className="flex flex-1 flex-col justify-end gap-0.5">
                  <div className="rounded-sm bg-indigo-400/70" style={{ height: `${10 + i * 2.2}px` }} />
                  <div className="rounded-sm bg-violet-500" style={{ height: `${14 + i * 3.6 + (i % 3) * 3}px` }} />
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 lg:col-span-2">
            <p className="text-[12px] text-slate-400">Work log</p>
            <ul className="mt-3 space-y-2.5">
              {["Technical SEO", "Content", "Authority", "AI Search"].map((w, i) => (
                <li key={w} className="flex items-center justify-between text-[12.5px] text-slate-200">
                  <span className="flex items-center gap-2"><span className={`h-1.5 w-1.5 rounded-full ${i < 2 ? "bg-emerald-400" : "bg-violet-400"}`} />{w}</span>
                  <span className="font-mono text-[10.5px] text-slate-400">{i < 2 ? "Done" : "In progress"}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const ReportingDashboard = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "center center"] });
  const rotateX = useTransform(scrollYProgress, [0, 1], [22, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [0.9, 1]);
  return (
    <section ref={ref} data-testid="reporting-dashboard-section" className="grain relative overflow-hidden bg-navy py-24 text-white sm:py-32">
      <div aria-hidden="true" className="absolute left-1/2 top-1/3 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-violet-700/25 blur-[140px]" />
      <div className="wrap relative">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal><Label n="07" dark>Reporting platform</Label></Reveal>
            <Reveal delay={0.05}><H2 dark className="mt-6">Everything we do. <span className="font-serif-i text-gradient">Visible in one place.</span></H2></Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-5 lg:col-start-8">
            <Paras dark items={["We have built a dedicated reporting platform for our clients. Your reporting dashboard shows everything we are doing for your business, so you always know what is happening with your search visibility."]} />
            <div className="mt-8"><CtaButton label="Ask for a Dashboard Walkthrough" testid="dashboard-cta" /></div>
          </Reveal>
        </div>
        <div className="mt-16 [perspective:1600px]">
          <motion.div style={{ rotateX, scale, transformOrigin: "50% 0%" }} data-testid="dashboard-mock">
            <Mock />
          </motion.div>
          <p className="mt-4 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Illustrative preview</p>
        </div>
        <div className="mt-16 grid gap-4 md:grid-cols-3">
          {POINTS.map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.08} className="rounded-3xl border border-white/10 bg-white/[0.03] p-7">
              <span className="font-mono text-xs text-violet-300">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-6 font-display text-2xl font-medium">{t}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-300">{d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
